// src/components/auth/LoginPage.jsx
import { useState, useEffect } from 'react';
import {
  signIn,
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  signInWithRedirect,
  resendSignUpCode,
} from 'aws-amplify/auth';
import { Eye, EyeOff } from 'lucide-react';
import styles from '../../styles/LoginPage.module.css';
import { VITE_COGNITO_DOMAIN } from '../../config';

// SVGs live in /public; Vite serves that directory at the site root.
const routeAiLogo  = '/route-ai.svg';
const googleGLetter = '/google-g-letter.svg';

// Google OAuth is only active once a Cognito hosted domain is configured
const GOOGLE_ENABLED = !!VITE_COGNITO_DOMAIN;

// ─── Icons ───────────────────────────────────────────────────────────────────

const GoogleIcon = () => (
  <img src={googleGLetter} alt="" className={styles.socialIcon} aria-hidden="true" />
);

// ─── Small reusable pieces ────────────────────────────────────────────────────

function PasswordField({ id, label, value, onChange, placeholder, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className={styles.formGroup}>
      <label htmlFor={id} className={styles.label}>{label}</label>
      <div className={styles.inputWrapper}>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className={styles.input}
          autoComplete={autoComplete || 'current-password'}
          required
        />
        <button
          type="button"
          className={styles.eyeButton}
          onClick={() => setShow(s => !s)}
          aria-label={show ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

function PasswordRequirements({ password }) {
  if (!password) return null;

  const checks = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'One uppercase letter',  met: /[A-Z]/.test(password) },
    { label: 'One lowercase letter',  met: /[a-z]/.test(password) },
    { label: 'One number',            met: /\d/.test(password) },
    { label: 'One special character', met: /[^A-Za-z0-9]/.test(password) },
  ];

  return (
    <ul className={styles.requirements} aria-label="Password requirements">
      {checks.map(c => (
        <li key={c.label} className={c.met ? styles.requirementMet : styles.requirementUnmet}>
          <span className={styles.requirementDot} aria-hidden="true">{c.met ? '✓' : '○'}</span>
          {c.label}
        </li>
      ))}
    </ul>
  );
}

function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div className={styles.errorBanner} role="alert" aria-live="assertive">
      <span>{message}</span>
      <button className={styles.dismissButton} onClick={onDismiss} aria-label="Dismiss error">×</button>
    </div>
  );
}

function SuccessBanner({ message }) {
  if (!message) return null;
  return (
    <div className={styles.successBanner} role="status" aria-live="polite">
      {message}
    </div>
  );
}

// ─── Error mapping ────────────────────────────────────────────────────────────

const FRIENDLY_ERRORS = {
  NotAuthorizedException:    'Incorrect email or password.',
  UserNotFoundException:     'No account found with this email.',
  UsernameExistsException:   'An account with this email already exists.',
  InvalidPasswordException:  'Password does not meet the requirements.',
  CodeMismatchException:     'Invalid verification code. Please try again.',
  ExpiredCodeException:      'The code has expired. Please request a new one.',
  LimitExceededException:    'Too many attempts. Please wait a moment and try again.',
  UserNotConfirmedException: 'Please verify your email before signing in.',
  InvalidParameterException: 'Please check your input and try again.',
};

function friendlyError(err) {
  return FRIENDLY_ERRORS[err?.name] || err?.message || 'Something went wrong. Please try again.';
}

// ─── Legal modal ─────────────────────────────────────────────────────────────

function LegalSection({ title, children }) {
  return (
    <section className={styles.legalSection}>
      <h3 className={styles.legalSectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

function TermsContent() {
  return (
    <>
      <p className={styles.legalEffectiveDate}>Effective Date: July 19, 2026</p>

      <LegalSection title="1. Acceptance of Terms">
        <p>By creating an account or using Aridel ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service. These Terms form a legally binding agreement between you and Aridel.</p>
      </LegalSection>

      <LegalSection title="2. Description of Service">
        <p>Aridel is an AI prompt-routing platform that intelligently forwards your queries to one or more third-party large language model (LLM) providers and returns their responses. We do not generate AI responses ourselves; we facilitate access to external AI models on your behalf. The providers that serve a given request — including, for open-source models served on Aridel's own provider accounts, providers operating under Chinese jurisdiction — are chosen by Aridel and disclosed in our Privacy Policy; no server-location or data-residency guarantee is offered unless expressly stated in writing.</p>
      </LegalSection>

      <LegalSection title="3. Account Registration">
        <p>You must provide accurate, complete information when registering and keep your credentials confidential. You are responsible for all activity that occurs under your account. Notify us immediately at <a href="mailto:contact@aridel.ai" className={styles.legalAnchor}>contact@aridel.ai</a> if you suspect unauthorized access.</p>
      </LegalSection>

      <LegalSection title="4. Acceptable Use">
        <p>You agree not to use the Service to:</p>
        <ul className={styles.legalList}>
          <li>Generate or distribute content that is illegal, harmful, defamatory, or violates any third-party rights.</li>
          <li>Produce child sexual abuse material (CSAM) or any content that exploits or endangers minors.</li>
          <li>Circumvent the safety filters or usage policies of any underlying AI provider.</li>
          <li>Reverse-engineer, scrape, or attempt to extract model weights or proprietary system prompts.</li>
          <li>Use the Service for spam, phishing, or any other unauthorized bulk messaging.</li>
          <li>Violate any applicable law or regulation.</li>
        </ul>
        <p>We reserve the right to suspend or terminate accounts that violate this policy without prior notice.</p>
      </LegalSection>

      <LegalSection title="5. AI-Generated Content Disclaimer">
        <p>Responses delivered through Aridel are generated by third-party AI models and may be inaccurate, incomplete, or outdated. You are solely responsible for verifying any AI-generated output before relying on it for professional, medical, legal, financial, or other consequential decisions. Aridel makes no representation regarding the accuracy or fitness of AI-generated content for any particular purpose.</p>
      </LegalSection>

      <LegalSection title="6. Intellectual Property">
        <p>You retain ownership of the prompts and content you submit. By submitting content, you grant Aridel a limited, non-exclusive license to process and route that content solely for the purpose of delivering the Service. Aridel and its licensors retain all rights to the platform, software, and branding.</p>
      </LegalSection>

      <LegalSection title="7. Payments">
        <p>Certain features of the Service require prepaid usage credits, purchased one-time and non-refundable except where required by applicable law. We reserve the right to change pricing with at least 30 days' notice. Continued use after a price change constitutes acceptance of the new pricing.</p>
      </LegalSection>

      <LegalSection title="8. Disclaimer of Warranties">
        <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.</p>
      </LegalSection>

      <LegalSection title="9. Limitation of Liability">
        <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ARIDEL AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR DATA, ARISING OUT OF YOUR USE OF THE SERVICE. OUR TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE MONTHS PRECEDING THE CLAIM.</p>
      </LegalSection>

      <LegalSection title="10. Termination">
        <p>We may suspend or terminate your account at any time for violation of these Terms or for any other reason at our sole discretion. You may close your account at any time by contacting support. Upon termination, your right to use the Service immediately ceases.</p>
      </LegalSection>

      <LegalSection title="11. Governing Law">
        <p>These Terms are governed by and construed in accordance with the laws of Switzerland, without regard to conflict-of-law principles. Any dispute arising from these Terms shall be resolved exclusively in the courts located in Switzerland.</p>
      </LegalSection>

      <LegalSection title="12. Changes to These Terms">
        <p>We may update these Terms from time to time. We will notify you of material changes by email or by an in-app notice at least 14 days before the changes take effect. Continued use of the Service after the effective date constitutes acceptance of the revised Terms.</p>
      </LegalSection>

      <LegalSection title="13. Contact Us">
        <p>Questions about these Terms? Reach us at <a href="mailto:contact@aridel.ai" className={styles.legalAnchor}>contact@aridel.ai</a>.</p>
      </LegalSection>
    </>
  );
}

function PrivacyContent() {
  return (
    <>
      <p className={styles.legalEffectiveDate}>Effective Date: July 19, 2026</p>

      <LegalSection title="1. Introduction">
        <p>Aridel ("we," "us," or "our") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI prompt-routing platform. Please read it carefully before using the Service.</p>
      </LegalSection>

      <LegalSection title="2. Information We Collect">
        <p><strong>Account information:</strong> When you register, we collect your email address and, optionally, your name. This information is managed through AWS Cognito, a secure cloud identity service.</p>
        <p><strong>Usage data:</strong> We collect metadata about your sessions — including prompt counts, routing decisions, timestamps, and feature usage — to operate and improve the Service.</p>
        <p><strong>Prompt content:</strong> The text of your prompts is transmitted to the selected AI provider(s) to generate a response. We may retain prompts for a limited period for debugging, safety review, and service improvement unless you request deletion.</p>
        <p><strong>Device and log data:</strong> We automatically collect IP addresses, browser type, operating system, and referral URLs via standard web server logs.</p>
      </LegalSection>

      <LegalSection title="3. How We Use Your Information">
        <ul className={styles.legalList}>
          <li>To authenticate you and manage your account.</li>
          <li>To route your prompts to the appropriate AI model and return responses.</li>
          <li>To monitor for abuse, enforce our Terms of Service, and maintain security.</li>
          <li>To analyze usage patterns and improve the platform.</li>
          <li>To send transactional emails (e.g., email verification, password resets).</li>
          <li>To comply with legal obligations.</li>
        </ul>
        <p>We do not sell your personal information to third parties.</p>
      </LegalSection>

      <LegalSection title="4. Third-Party AI Providers and Data Jurisdiction">
        <p>Aridel routes your prompts to one or more third-party LLM providers (such as OpenAI, Anthropic, Google, and others). When your prompt is forwarded, it is subject to that provider's own privacy policy and terms of service. We encourage you to review their policies. We do not control how third-party providers use or store data they receive.</p>
        <p><strong>Where inference happens:</strong> the Aridel platform itself (accounts, history, billing, routing) runs in AWS (United States). Open-source models served on Aridel's own provider accounts may be processed by inference providers operating under <strong>Chinese jurisdiction</strong> — currently Z.AI (Zhipu AI) and Alibaba Cloud Model Studio — as well as by Fireworks AI and Amazon Bedrock in the United States. Prompts and responses sent to those models are processed under the serving provider's local law; <strong>no EU or US data-residency guarantee applies</strong>. If you require residency control, supply your own provider keys (BYOK): requests made with your keys go only to the provider and region your key is scoped to.</p>
      </LegalSection>

      <LegalSection title="5. Data Sharing and Disclosure">
        <p>We do not share your personal information except:</p>
        <ul className={styles.legalList}>
          <li><strong>With AI providers:</strong> As described above, solely to fulfill your requests.</li>
          <li><strong>With service providers:</strong> Trusted vendors (e.g., AWS) who assist us in operating the Service, under strict confidentiality obligations.</li>
          <li><strong>For legal reasons:</strong> When required by law, subpoena, or to protect the rights and safety of our users or others.</li>
          <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, with prior notice to you.</li>
        </ul>
      </LegalSection>

      <LegalSection title="6. Data Retention">
        <p>We retain your account information for as long as your account is active. Prompt content logs are retained for up to 90 days for operational and safety purposes, after which they are deleted or anonymized. You may request earlier deletion by contacting us.</p>
      </LegalSection>

      <LegalSection title="7. Data Security">
        <p>We use industry-standard safeguards including encryption in transit (TLS 1.2+), encryption at rest, and strict access controls to protect your data. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.</p>
      </LegalSection>

      <LegalSection title="8. Your Rights">
        <p>Depending on your location, you may have rights under applicable privacy laws (including GDPR and CCPA) to:</p>
        <ul className={styles.legalList}>
          <li>Access a copy of the personal information we hold about you.</li>
          <li>Correct inaccurate or incomplete data.</li>
          <li>Request deletion of your personal information ("right to be forgotten").</li>
          <li>Object to or restrict certain processing activities.</li>
          <li>Receive your data in a portable, machine-readable format.</li>
          <li>Opt out of the "sale" or "sharing" of personal information (we do not sell data).</li>
        </ul>
        <p>To exercise any of these rights, contact us at <a href="mailto:contact@aridel.ai" className={styles.legalAnchor}>contact@aridel.ai</a>. We will respond within 30 days.</p>
      </LegalSection>

      <LegalSection title="9. Cookies and Tracking">
        <p>We use only essential session cookies required for authentication and security. We do not use third-party advertising or tracking cookies. You can configure your browser to refuse cookies, but some features may not function correctly without them.</p>
      </LegalSection>

      <LegalSection title="10. Children's Privacy">
        <p>The Service is not directed to children under 13 (or 16 in the EU). We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, contact us immediately and we will delete it promptly.</p>
      </LegalSection>

      <LegalSection title="11. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. We will notify you of material changes by email or by an in-app notice at least 14 days before the changes take effect. The "Effective Date" at the top of this policy reflects the most recent revision.</p>
      </LegalSection>

      <LegalSection title="12. Contact Us">
        <p>For privacy-related inquiries, contact our Data Protection team at <a href="mailto:contact@aridel.ai" className={styles.legalAnchor}>contact@aridel.ai</a>.</p>
      </LegalSection>
    </>
  );
}

function LegalModal({ which, onClose }) {
  useEffect(() => {
    const triggerEl = document.activeElement;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
      triggerEl?.focus();
    };
  }, [onClose]);

  const isTerms = which === 'terms';

  return (
    <div className={styles.modalBackdrop} onClick={onClose} role="presentation">
      <div
        className={styles.modalDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="legalModalTitle"
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 id="legalModalTitle" className={styles.modalTitle}>
            {isTerms ? 'Terms of Service' : 'Privacy Policy'}
          </h2>
          <button
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Close"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          >
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          {isTerms ? <TermsContent /> : <PrivacyContent />}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const TAGLINES = {
  signIn:        'Route every prompt to the best AI model',
  signUp:        'Create your account',
  confirmSignUp: 'Verify your email',
  forgotPassword:'Reset your password',
  resetPassword: 'Create a new password',
};

export default function LoginPage({ oauthError = '' }) {
  const [view, setView] = useState('signIn');

  // Shared fields (email persists across views so users don't have to retype it)
  const [email,              setEmail]              = useState('');
  const [password,           setPassword]           = useState('');
  const [name,               setName]               = useState('');
  const [confirmPassword,    setConfirmPassword]    = useState('');
  const [confirmCode,        setConfirmCode]        = useState('');
  const [newPassword,        setNewPassword]        = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const [isLoading,      setIsLoading]      = useState(false);
  const [isResending,    setIsResending]    = useState(false);
  const [error,          setError]          = useState(oauthError);
  const [successMessage, setSuccessMessage] = useState('');
  const [legalOpen,      setLegalOpen]      = useState(null); // 'terms' | 'privacy' | null

  // Sync Hub-triggered OAuth errors that arrive after this component is already mounted
  useEffect(() => {
    if (oauthError) setError(oauthError);
  }, [oauthError]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const clearMessages = () => { setError(''); setSuccessMessage(''); };

  const switchTo = (newView) => {
    clearMessages();
    setView(newView);
    // Reset per-view fields but keep email
    setPassword('');
    setConfirmPassword('');
    setConfirmCode('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  // ── Auth handlers ──────────────────────────────────────────────────────────

  const handleSignIn = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsLoading(true);
    try {
      const result = await signIn({ username: email.trim(), password });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        setView('confirmSignUp');
        setSuccessMessage(`Please verify your email to continue. Check ${email} for a code.`);
      }
      // On success: Hub fires 'signedIn' → App.jsx picks it up → shows the app
    } catch (err) {
      if (err.name === 'UserNotConfirmedException') {
        setView('confirmSignUp');
        setSuccessMessage(`Your email is not yet verified. Check ${email} for a code.`);
      } else {
        setError(friendlyError(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    clearMessages();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    try {
      await signUp({
        username: email.trim(),
        password,
        options: {
          userAttributes: {
            email: email.trim(),
            name: name.trim() || email.split('@')[0],
          },
        },
      });
      setView('confirmSignUp');
      setSuccessMessage(`We sent a 6-digit code to ${email}.`);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsLoading(true);
    try {
      await confirmSignUp({ username: email.trim(), confirmationCode: confirmCode.trim() });
      setView('signIn');
      setSuccessMessage('Email verified! You can now sign in.');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsLoading(true);
    try {
      await resetPassword({ username: email.trim() });
      setView('resetPassword');
      setSuccessMessage(`We sent a reset code to ${email}.`);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: confirmCode.trim(),
        newPassword,
      });
      setView('signIn');
      setSuccessMessage('Password reset successfully! You can now sign in.');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    clearMessages();
    setIsResending(true);
    try {
      await resendSignUpCode({ username: email.trim() });
      setSuccessMessage('A new code has been sent to your email.');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsResending(false);
    }
  };

  const handleGoogleSignIn = async () => {
    clearMessages();
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (err) {
      setError(friendlyError(err));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
    <div className={styles.page}>
      <div className={styles.card}>

        {/* Brand header */}
        <div className={styles.brand}>
          <div className={styles.brandLogoWrap}>
            <img src={routeAiLogo} alt="Arianna logo" className={styles.brandIcon} />
          </div>
          <h1 className={styles.brandName}>Arianna</h1>
          <p className={styles.brandTagline}>{TAGLINES[view]}</p>
        </div>

        {/* Feedback banners */}
        <ErrorBanner message={error} onDismiss={() => setError('')} />
        <SuccessBanner message={successMessage} />

        {/* ── Sign In ───────────────────────────────────────────────────────── */}
        {view === 'signIn' && (
          <>
            <form onSubmit={handleSignIn} noValidate>
              <div className={styles.formGroup}>
                <label htmlFor="signInEmail" className={styles.label}>Email</label>
                <input
                  id="signInEmail"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={styles.input}
                  autoComplete="email"
                  required
                />
              </div>

              <PasswordField
                id="signInPassword"
                label="Password"
                value={password}
                onChange={setPassword}
                placeholder="Enter your password"
                autoComplete="current-password"
              />

              <div className={styles.forgotRow}>
                <button type="button" className={styles.linkButton} onClick={() => switchTo('forgotPassword')}>
                  Forgot password?
                </button>
              </div>

              <button type="submit" className={styles.primaryButton} disabled={isLoading}>
                {isLoading ? <span className={styles.spinner} aria-label="Signing in…" /> : 'Sign In'}
              </button>
            </form>

            {GOOGLE_ENABLED && (
              <>
                <div className={styles.divider}><span>or</span></div>
                <button type="button" className={styles.googleButton} onClick={handleGoogleSignIn}>
                  <GoogleIcon />
                  Continue with Google
                </button>
              </>
            )}

            <p className={styles.switchText}>
              Don't have an account?{' '}
              <button type="button" className={styles.linkButton} onClick={() => switchTo('signUp')}>
                Create one
              </button>
            </p>
          </>
        )}

        {/* ── Sign Up ───────────────────────────────────────────────────────── */}
        {view === 'signUp' && (
          <>
            <form onSubmit={handleSignUp} noValidate>
              <div className={styles.formGroup}>
                <label htmlFor="signUpName" className={styles.label}>
                  Full Name <span className={styles.optional}>(optional)</span>
                </label>
                <input
                  id="signUpName"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name"
                  className={styles.input}
                  autoComplete="name"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="signUpEmail" className={styles.label}>Email</label>
                <input
                  id="signUpEmail"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={styles.input}
                  autoComplete="email"
                  required
                />
              </div>

              <PasswordField
                id="signUpPassword"
                label="Password"
                value={password}
                onChange={setPassword}
                placeholder="Create a strong password"
                autoComplete="new-password"
              />
              <PasswordRequirements password={password} />

              <PasswordField
                id="signUpConfirmPassword"
                label="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Repeat your password"
                autoComplete="new-password"
              />

              <button type="submit" className={styles.primaryButton} disabled={isLoading}>
                {isLoading ? <span className={styles.spinner} aria-label="Creating account…" /> : 'Create Account'}
              </button>
            </form>

            {GOOGLE_ENABLED && (
              <>
                <div className={styles.divider}><span>or</span></div>
                <button type="button" className={styles.googleButton} onClick={handleGoogleSignIn}>
                  <GoogleIcon />
                  Sign up with Google
                </button>
              </>
            )}

            <p className={styles.switchText}>
              Already have an account?{' '}
              <button type="button" className={styles.linkButton} onClick={() => switchTo('signIn')}>
                Sign in
              </button>
            </p>
          </>
        )}

        {/* ── Confirm Sign Up ───────────────────────────────────────────────── */}
        {view === 'confirmSignUp' && (
          <>
            <p className={styles.instructionText}>
              Enter the 6-digit code sent to <strong>{email || 'your email'}</strong>.
            </p>

            <form onSubmit={handleConfirmSignUp} noValidate>
              <div className={styles.formGroup}>
                <label htmlFor="confirmCode" className={styles.label}>Verification Code</label>
                <input
                  id="confirmCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={confirmCode}
                  onChange={e => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className={`${styles.input} ${styles.codeInput}`}
                  maxLength={6}
                  autoComplete="one-time-code"
                  required
                />
              </div>

              <button type="submit" className={styles.primaryButton} disabled={isLoading}>
                {isLoading ? <span className={styles.spinner} aria-label="Verifying…" /> : 'Verify Email'}
              </button>
            </form>

            <p className={styles.resendRow}>
              <span className={styles.mutedText}>Didn't receive it?</span>{' '}
              <button type="button" className={styles.linkButton} onClick={handleResendCode} disabled={isResending}>
                {isResending ? 'Sending…' : 'Resend code'}
              </button>
            </p>

            <p className={styles.switchText}>
              <button type="button" className={styles.linkButton} onClick={() => switchTo('signIn')}>
                ← Back to sign in
              </button>
            </p>
          </>
        )}

        {/* ── Forgot Password ───────────────────────────────────────────────── */}
        {view === 'forgotPassword' && (
          <>
            <p className={styles.instructionText}>
              Enter your email and we'll send you a reset code.
            </p>

            <form onSubmit={handleForgotPassword} noValidate>
              <div className={styles.formGroup}>
                <label htmlFor="forgotEmail" className={styles.label}>Email</label>
                <input
                  id="forgotEmail"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={styles.input}
                  autoComplete="email"
                  required
                />
              </div>

              <button type="submit" className={styles.primaryButton} disabled={isLoading}>
                {isLoading ? <span className={styles.spinner} aria-label="Sending…" /> : 'Send Reset Code'}
              </button>
            </form>

            <p className={styles.switchText}>
              <button type="button" className={styles.linkButton} onClick={() => switchTo('signIn')}>
                ← Back to sign in
              </button>
            </p>
          </>
        )}

        {/* ── Reset Password ────────────────────────────────────────────────── */}
        {view === 'resetPassword' && (
          <>
            <p className={styles.instructionText}>
              Enter the code sent to <strong>{email || 'your email'}</strong> and your new password.
            </p>

            <form onSubmit={handleResetPassword} noValidate>
              <div className={styles.formGroup}>
                <label htmlFor="resetCode" className={styles.label}>Reset Code</label>
                <input
                  id="resetCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={confirmCode}
                  onChange={e => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className={`${styles.input} ${styles.codeInput}`}
                  maxLength={6}
                  required
                />
              </div>

              <PasswordField
                id="newPassword"
                label="New Password"
                value={newPassword}
                onChange={setNewPassword}
                placeholder="Create a new password"
                autoComplete="new-password"
              />
              <PasswordRequirements password={newPassword} />

              <PasswordField
                id="confirmNewPassword"
                label="Confirm New Password"
                value={confirmNewPassword}
                onChange={setConfirmNewPassword}
                placeholder="Repeat your new password"
                autoComplete="new-password"
              />

              <button type="submit" className={styles.primaryButton} disabled={isLoading}>
                {isLoading ? <span className={styles.spinner} aria-label="Resetting…" /> : 'Reset Password'}
              </button>
            </form>

            <p className={styles.switchText}>
              <button type="button" className={styles.linkButton} onClick={() => switchTo('signIn')}>
                ← Back to sign in
              </button>
            </p>
          </>
        )}

        {/* Legal footer */}
        <p className={styles.legalText}>
          By continuing, you agree to our{' '}
          <button type="button" className={styles.legalLink} onClick={() => setLegalOpen('terms')}>
            Terms of Service
          </button>
          {' '}and{' '}
          <button type="button" className={styles.legalLink} onClick={() => setLegalOpen('privacy')}>
            Privacy Policy
          </button>
        </p>
      </div>
    </div>
    {legalOpen && <LegalModal which={legalOpen} onClose={() => setLegalOpen(null)} />}
    </>
  );
}
